import React, { useEffect, useRef, useState } from 'react';
import { Camera, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * CameraScanner — decode 1D/2D barcodes from the device camera.
 *
 * On iPad Safari 17+ and Chrome we use the native `BarcodeDetector` API.
 * On older browsers we lazy-load ZXing from a CDN as a graceful fallback.
 *
 * Emits `onDetect(code)` when a barcode is decoded. Stops the camera
 * stream automatically on unmount and after a successful detect.
 */
export default function CameraScanner({
  onDetect,
  formats = ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code'],
  testId = 'camera-scanner',
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const zxingRef = useRef(null);
  const [status, setStatus] = useState('starting'); // starting|scanning|error
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      // Camera permissions require HTTPS + a user gesture; Playwright's
      // headless Chromium may not grant it, which is fine — we surface
      // a clear error and the user still has the manual input.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch (e) {
        if (!cancelled) {
          setStatus('error');
          setError(e.message || 'Camera permission denied.');
        }
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      const v = videoRef.current;
      if (!v) return;
      v.srcObject = stream;
      v.setAttribute('playsinline', 'true');
      try { await v.play(); } catch { /* silent */ }
      setStatus('scanning');

      // Native BarcodeDetector path (iPad/iPhone Safari 17+, Chrome/Edge).
      if ('BarcodeDetector' in window) {
        const detector = new window.BarcodeDetector({ formats });
        const tick = async () => {
          if (cancelled || !videoRef.current) return;
          try {
            const results = await detector.detect(videoRef.current);
            if (results && results.length > 0) {
              const raw = (results[0].rawValue || '').trim();
              if (raw) {
                onDetect?.(raw);
                return;  // stop the loop — parent decides next step
              }
            }
          } catch { /* transient decode error — keep looping */ }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Fallback: ZXing-JS from CDN. Loaded once, cached on window.
      try {
        if (!window.__zxingReader) {
          const zxingCdnUrl = 'https://cdn.jsdelivr.net/npm/@zxing/browser@0.1.5/+esm';
          const mod = await import(/* webpackIgnore: true */ zxingCdnUrl);
          window.__zxingReader = new mod.BrowserMultiFormatReader();
        }
        zxingRef.current = window.__zxingReader;
        zxingRef.current.decodeFromVideoElementContinuously(
          v,
          (result, err, controls) => {
            if (result && !cancelled) {
              const raw = (result.getText?.() || '').trim();
              if (raw) {
                onDetect?.(raw);
                controls?.stop();
              }
            }
          },
        );
      } catch (e) {
        if (!cancelled) {
          setStatus('error');
          setError('Camera scan not supported on this browser.');
        }
      }
    }

    start();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (zxingRef.current && zxingRef.current.reset) {
        try { zxingRef.current.reset(); } catch { /* silent */ }
      }
    };
  }, [onDetect, formats]);

  return (
    <div className="relative bg-black rounded-lg overflow-hidden" data-testid={testId}>
      <video ref={videoRef} className="w-full h-64 object-cover" muted />
      {/* Overlay guide */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-3/4 h-24 border-2 border-emerald-400/80 rounded-md shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
      </div>
      <div className="absolute top-2 left-2 right-2 flex items-center justify-between text-xs text-white/90">
        <span className="inline-flex items-center gap-1 bg-black/40 rounded px-2 py-1">
          {status === 'starting' && <><Loader2 size={11} className="animate-spin" /> Starting camera…</>}
          {status === 'scanning' && <><Camera size={11} /> Point at the barcode</>}
          {status === 'error' && <><X size={11} /> {error}</>}
        </span>
      </div>
    </div>
  );
}
